import React from "react";
import PropTypes from "prop-types";
import * as RadixTooltip from "@radix-ui/react-tooltip";
import { twMerge } from "tailwind-merge";
import { motion, AnimatePresence } from "framer-motion";

import FinIcon from "../../icons/Fin";

const missingProvider = "Component must be used within <Tooltip.Root>";

const TooltipContext = React.createContext({
  get triggerRef() {
    throw new Error(missingProvider);
  },
  get tooltipProps() {
    throw new Error(missingProvider);
  },
  get tooltipState() {
    throw new Error(missingProvider);
  },
});

TooltipContext.displayName = "TooltipContext";

export function Provider({ children }) {
  return <RadixTooltip.Provider>{children}</RadixTooltip.Provider>;
}

export function Root({
  delay,
  defaultOpen,
  open,
  headless = false,
  children,
  onOpenChange,
  ...delegated
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : uncontrolledOpen;

  const handleOpenChange = (nextValue) => {
    if (!isControlled) {
      setUncontrolledOpen(nextValue);
    }
    onOpenChange?.(nextValue);
  };

  const providerValue = React.useMemo(
    () => ({
      isOpen,
      headless,
    }),
    [isOpen, headless],
  );

  return (
    <TooltipContext.Provider value={providerValue}>
      <RadixTooltip.Root
        delayDuration={delay}
        defaultOpen={defaultOpen}
        open={open}
        onOpenChange={handleOpenChange}
        {...delegated}
      >
        {children}
      </RadixTooltip.Root>
    </TooltipContext.Provider>
  );
}

export function Arrow({ className, ...delegated }) {
  return <RadixTooltip.Arrow {...delegated} className={className} />;
}

export function Panel({
  className,
  motionProps,
  style,
  children,
  ...delegated
}) {
  const { isOpen, headless } = React.useContext(TooltipContext);

  return (
    <AnimatePresence>
      {isOpen ? (
        <RadixTooltip.Content
          forceMount={!headless}
          asChild={true}
          {...delegated}
        >
          <motion.div
            initial={headless ? undefined : "closed"}
            animate={headless ? undefined : "enter"}
            exit={headless ? undefined : "closed"}
            variants={
              headless
                ? undefined
                : {
                    closed: {
                      opacity: 0,
                      scale: 0.95,
                      transition: {
                        duration: 0.1,
                        ease: "easeIn",
                      },
                    },
                    enter: {
                      opacity: 1,
                      scale: 1,
                      transition: {
                        duration: 0.2,
                        ease: "easeOut",
                      },
                    },
                  }
            }
            style={{
              ...style,
              transformOrigin: "var(--radix-popover-content-transform-origin)",
            }}
            {...motionProps}
          >
            <div className={twMerge(!headless && "bg-gray-100", className)}>
              {children}
            </div>

            {!headless && (
              <Arrow width={24} height={10} asChild={true}>
                <FinIcon className="w-[24px] h-[10px] text-gray-100" />
              </Arrow>
            )}
          </motion.div>
        </RadixTooltip.Content>
      ) : null}
    </AnimatePresence>
  );
}

export function Trigger({ onPress, children, ...delegated }) {
  return (
    <RadixTooltip.Trigger onClick={onPress} {...delegated}>
      {children}
    </RadixTooltip.Trigger>
  );
}

Provider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

Root.propTypes = {
  delay: PropTypes.number,
  defaultOpen: PropTypes.bool,
  open: PropTypes.bool,
  headless: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  onOpenChange: PropTypes.func,
};

Arrow.propTypes = {
  className: PropTypes.string,
};

Panel.propTypes = {
  className: PropTypes.string,
  motionProps: PropTypes.object,
  style: PropTypes.object,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

Trigger.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  onPress: PropTypes.func,
};
